import { computed, inject, Injectable, Signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { UiApiService } from "./ui-api.service";
import { UiStoreService, UserInfosState } from "./ui-store.service";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { UserInfos } from "../../models/user_infos.model";
import { Role } from "../../../auth/enums/role.enum";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";

@Injectable({ providedIn: "root" })
export class UiFacadeService extends EntityFacadeService<
  UserInfos,
  UiApiService,
  UserInfosState,
  EntityMetadata,
  UiStoreService
> {
  constructor() {
    const api = inject(UiApiService);
    const store = inject(UiStoreService);
    super(api, store);
    this.store.initStatus(UiFacadeService);
  }

  getByUserId(userId: string): Signal<UserInfos> {
    return this.store.getByUserId(userId);
  }

  /**
   * Les utilisateurs qu'on peut **proposer** dans un sélecteur — sans les comptes
   * techniques (`Role.API`), dont « Alertes Qualité », qui signe les NC ouvertes
   * automatiquement.
   *
   * ⚠️ **Filtrer ici, pas dans le store ni côté back.** Ces comptes doivent rester
   * chargés : ils sont l'AUTEUR de lignes déjà enregistrées, et les retirer de la
   * liste ferait afficher un auteur vide sur chaque alerte qualité. La distinction
   * est « affichable, mais pas assignable ».
   */
  selectableUserInfos = computed(() =>
    (this.getAllEntities()() ?? []).filter((u) => u.user?.role !== Role.API),
  );

  async loadByUserId(userId: string) {
    const ui = await this.api.loadOneByUserId(userId);
    this.store.upsertOne(ui);
  }

  async loadCurrent() {
    const ui = await this.api.loadCurrent();
    if (!ui) return;
    this.store.getWritableState().currentUserId.set(ui.userId);
    this.store.upsertOne(ui);
  }

  currentUserInfos = computed(() => {
    const currentId = this.getState().currentUserId();
    if (currentId) {
      return this.getByUserId(currentId)?.();
    }
  });

  private dialogs = inject(DialogsService);

  initState() {
    this.store.patchState({
      stats: null,
    });
  }

  @Untrack
  @UpdateStatus
  async stats(payload: {
    userInfosId: string;
    dateDebut: Date;
    dateFin: Date;
  }) {
    const stats = await firstValueFrom(this.api.stats(payload));
    this.store.getWritableState().stats.set(stats);
  }

  async onStats(userInfosId: string) {
    const { StatsUserInfosDialogComponent } = await import(
      "../../containers/stats-user-infos-dialog/stats-user-infos-dialog.component"
    );
    this.dialogs.open(StatsUserInfosDialogComponent, "95dvw", "95dvh", {
      userInfosId,
    });
  }
}
